import { EditOutlined, InfoOutlined, WechatOutlined } from "@ant-design/icons";
import { Button, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import CheckAccess from "hoc/CheckAccess";
import useResponsive from "hooks/useResponsive";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { translate } from "utils";
import {
	getListMerchantRequestAsync,
	selectMerchantRequestList,
	selectMerchantRequestTotal,
} from "./redux/merchantRequest.slice";
import MerchantRequestSearch from "./MerchantRequestSearch";
import { useDispatch } from "react-redux";
import { selectLang } from "store/lang/langSlice";
import {
	selectMerchantRequestFilter,
	setMerchantRequestFilter,
} from "store/filter/index.slice";
import { formatCapital } from "utils/format";
import {
	MERCHANT_REQUEST_STATUS,
	MerchantRequest,
	MerchantRequestQueryParams,
} from "./type";
import { Merchant } from "features/merchant/types";
import { MerchantQuery } from "service/queries";
import { merchantRequestStatus } from "./constant";
import { PERMISSION } from "../../constants";
import { setBreadCrumbs } from "store/app/app.slice";
import { APP_PATH } from "constants/path";

export interface MerchantQueryFilter extends MerchantQuery {
	isRemoved: boolean;
}

const MerchantRequestList = () => {
	const dispatch = useDispatch();
	const language = useSelector(selectLang);
	const { isMobile } = useResponsive();

	const merchantRequestFilter = useSelector(selectMerchantRequestFilter);
	const [query, setQuery] = useState<MerchantRequestQueryParams>({
		...merchantRequestFilter,
		language,
	});
	const [queryMerchant, setQueryMerchant] = useState<MerchantQueryFilter>({
		skip: 0,
		take: 10,
		total: 0,
		isRemoved: false,
	});
	const [merchantList, setMerchantList] = useState<Merchant[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const merchantRequestList = useSelector(selectMerchantRequestList);
	const merchantRequestTotal = useSelector(selectMerchantRequestTotal);

	const columns: ColumnsType<MerchantRequest> = [
		{
			title: translate("NO"),
			dataIndex: "",
			key: "NO",
			width: "50px",
			align: "center",
			render: (value, record, index: number) => (
				<p>{query.skip * query.take + index + 1}</p>
			),
		},
		{
			title: translate("Request ID"),
			dataIndex: "id",
			key: "id",
			render: value => (
				<Link
					to={`${APP_PATH.transactionMerchantRequest}/${value}`}
					className="text-blue-500 text-center"
				>
					{value}
				</Link>
			),
		},
		{
			title: translate("Merchant"),
			dataIndex: "merchant",
			key: "merchant",
			render: (merchant, record) => <div>{merchant.fullName}</div>,
		},
		{
			title: translate("Type"),
			dataIndex: "type",
			key: "type",
			render: value => <div>{translate(formatCapital(value))}</div>,
		},
		{
			title: translate("Description"),
			dataIndex: "name",
			key: "name",
			render: value => translate(value || ""),
		},
		{
			title: translate("Request date"),
			dataIndex: "createdAt",
			key: "createdAt",
			render: createdAt => <p>{moment(createdAt).format("DD/MM/YYYY")}</p>,
		},
		{
			title: translate("Status"),
			dataIndex: "status",
			key: "status",
			render: (status, record) => {
				const statusFound = merchantRequestStatus.find(
					el => el.value === status,
				);
				if (statusFound) {
					return (
						<div className="flex justify-center flex-wrap">
							<Tag color={statusFound.color}>
								{translate(statusFound.label)}
							</Tag>
						</div>
					);
				}
			},
		},
		{
			title: translate("Action"),
			key: "operation",
			fixed: isMobile ? false : "right",
			width: isMobile ? "70px" : "130px",
			render: (text, record) => (
				<div className="flex justify-center">
					<Link to={`${APP_PATH.transactionMerchantRequest}/${record.id}`}>
						<Button
							type="primary"
							className="m-1 flex items-center justify-center bg-[#1890ff]"
							icon={
								<CheckAccess
									perms={[PERMISSION.TRANSACTIONS.UPDATE]}
									componentReplace={<InfoOutlined />}
								>
									{record.status === MERCHANT_REQUEST_STATUS.REMOVED ? (
										<InfoOutlined />
									) : (
										<EditOutlined />
									)}
								</CheckAccess>
							}
							shape="circle"
							size={isMobile ? "small" : "middle"}
						/>
					</Link>
				</div>
			),
		},
	];

	useEffect(() => {
		dispatch(
			getListMerchantRequestAsync({
				query,
				callback: () => {
					setIsLoading(false);
				},
			}),
		);

		setBreadCrumbs([
			{
				label: translate("Merchant request"),
				path: "/merchant-request",
				icon: <WechatOutlined />,
			},
		]);
		// return cleanup function here
	}, []);

	useEffect(() => {
		const storedQuery = { ...query };

		storedQuery.merchantName = getStoredName(
			storedQuery.merchant,
			storedQuery.merchantName,
		);

		dispatch(setMerchantRequestFilter(formatQuery(storedQuery)));
	}, [query]);

	const getStoredName = (value?: string | number | null, name?: string) => {
		if (typeof value === "string") {
			return value;
		} else if (!value) {
			return undefined;
		} else {
			return name;
		}
	};

	const formatQuery = (query: MerchantRequestQueryParams) => {
		const filter = { ...query };
		// change merchant filter value: `fullname username` --> id
		if (typeof filter.merchant === "string") {
			filter.merchant =
				merchantList.filter(
					merchant =>
						`${merchant.fullName} ${merchant.username}` === filter.merchant,
				)[0]?.id || null;
		}
		return filter;
	};

	const getListMerchantRequest = (query: MerchantRequestQueryParams) => {
		setIsLoading(true);
		const keptQuery = { ...query };
		const callback = () => {
			setIsLoading(false);
			setQuery(keptQuery);
			dispatch(setMerchantRequestFilter(keptQuery));
		};
		query = formatQuery(query);
		dispatch(getListMerchantRequestAsync({ query, callback }));
	};

	const handleChangePage = (page: number, pageSize: number) => {
		if (pageSize === query.take) {
			const newQuery = {
				...query,
				skip: page - 1,
			};
			getListMerchantRequest(newQuery);
		}
	};

	const handlePageSizeChange = (_: number, size: number) => {
		const newQuery = { ...query, skip: 0, take: size };
		getListMerchantRequest(newQuery);
	};
	return (
		<div className=" h-full bg-white p-5 my-5 fade-in">
			<MerchantRequestSearch
				query={query}
				setQuery={setQuery}
				setLoading={setIsLoading}
				merchantList={merchantList}
				setMerchantList={setMerchantList}
				queryMerchant={queryMerchant}
				setQueryMerchant={setQueryMerchant}
				getListMerchantRequest={getListMerchantRequest}
			/>
			<div className="mt-8">
				<Table
					loading={isLoading}
					dataSource={merchantRequestList}
					bordered
					scroll={{ x: 800 }}
					columns={columns}
					rowKey={record => record.id}
					pagination={{
						pageSizeOptions: ["5", "10", "20", "50"],
						total: merchantRequestTotal,
						current: query.skip + 1,
						pageSize: query.take,
						showSizeChanger: true,
						showTotal: total =>
							translate("Total") + " " + total + " " + translate("results"),
						onChange: handleChangePage,
						onShowSizeChange: handlePageSizeChange,
					}}
					locale={{
						emptyText: (
							<span className="text-gray-8">{translate("No data")}</span>
						),
					}}
				/>
			</div>
		</div>
	);
};

export default MerchantRequestList;
